"use client";

import React, { useRef, useState, useEffect } from 'react';
import { useCharacter } from './CharacterProvider';
import { motion } from "framer-motion";
import ThumbValueSlider from './ThumbValueSlider';
import { useTheme } from '../../../contexts/ThemeProvider';

// Компонент для отображения слайдеров персонажа (правый блок)
export const CharacterSliders: React.FC = () => {
  const { character, updateCharacter } = useCharacter();
  const { isDark } = useTheme();
  const [errorCount, setErrorCount] = useState(0);
  const [animateError, setAnimateError] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const errorSound = useRef<HTMLAudioElement | null>(null);
  
  // Добавляем логирование для отладки
  useEffect(() => {
    console.log('CharacterSliders: character updated', character);
  }, [character]);
  
  // Инициализация звука ошибки
  useEffect(() => {
    errorSound.current = new Audio('/audio/windowsErrorMSG.wav');
    return () => {
      // Очистка при размонтировании
      if (errorSound.current) {
        errorSound.current.pause();
        errorSound.current = null;
      }
    };
  }, []);
  
  // Проверяем наличие иконок в зависимости от темы
  const getIconPath = (baseName: string, isDarkTheme: boolean): string => {
    // Используем суффикс B для темной темы или W для светлой
    const themeSuffix = isDarkTheme ? 'B' : 'W';
    return `${baseName}${themeSuffix}.png`;
  };
  
  // Рассчитываем общий вес характеристик
  const totalWeight = character.HighKick + character.punch + character.block + character.evasion + character.LowKick;

  // Обработчик попытки превысить максимальное значение
  const handleMaxLimitReached = () => {
    if (totalWeight >= 100) {
      // Увеличиваем счетчик ошибок
      setErrorCount(prev => prev + 1);
      
      // Проигрываем звук на каждую третью попытку
      if ((errorCount + 1) % 3 === 0 && errorSound.current) {
        errorSound.current.play().catch(e => console.error("Ошибка воспроизведения звука:", e));
      }
      
      // Активируем анимацию тряски
      setAnimateError(true);
      
      // Показываем сообщение об ошибке
      setShowErrorMessage(true);
      
      // Через некоторое время убираем анимацию и сообщение
      setTimeout(() => {
        setAnimateError(false);
        setTimeout(() => setShowErrorMessage(false), 2000);
      }, 500);
    }
  };

  return (
    <div className="w-full md:w-1/3 rounded-lg border border-border bg-gradient-to-b from-card to-card/90 text-card-foreground shadow-lg">
      <div className="p-6">
        <h3 className="text-2xl font-semibold mb-12">Action Chance</h3>
        
        <div className="space-y-8">
          <ThumbValueSlider 
            name="Удар рукой" 
            value={character.punch} 
            onChange={(value) => updateCharacter('punch', value)} 
            icon={getIconPath('punch', isDark)}
            maxValue={40}
            max={40}
            onMaxLimitReached={handleMaxLimitReached}
          />
          <ThumbValueSlider 
            name="Удар ногой" 
            value={character.HighKick} 
            onChange={(value) => updateCharacter('HighKick', value)} 
            icon={getIconPath('highKick', isDark)}
            maxValue={40}
            max={40}
            onMaxLimitReached={handleMaxLimitReached}
          />
          <ThumbValueSlider 
            name="Удар ногой в корпус" 
            value={character.LowKick} 
            onChange={(value) => updateCharacter('LowKick', value)} 
            icon={getIconPath('lowKick', isDark)}
            maxValue={40}
            max={40}
            onMaxLimitReached={handleMaxLimitReached}
          />
          <ThumbValueSlider 
            name="Блок" 
            value={character.block} 
            onChange={(value) => updateCharacter('block', value)} 
            icon={getIconPath('Block', isDark)}
            maxValue={40}
            max={40}
            onMaxLimitReached={handleMaxLimitReached}
          />
          <ThumbValueSlider 
            name="Уклонение" 
            value={character.evasion} 
            onChange={(value) => updateCharacter('evasion', value)} 
            icon={getIconPath('evasion', isDark)}
            maxValue={40}
            max={40}
            onMaxLimitReached={handleMaxLimitReached}
          />
        </div>
        
        <div className="mt-8 pt-4 border-t border-border">
          <div className="flex justify-between items-center mb-2">
            <span className="text-base font-medium">Total Weight:</span>
            <motion.span 
              animate={animateError ? {
                scale: [1, 1.1, 1],
                color: ["#ef4444", "#ffffff", "#ef4444"]
              } : {}}
              className={`text-xl font-bold ${totalWeight > 100 ? 'text-red-500' : ''}`}
            >
              {totalWeight}/100
            </motion.span>
          </div>
          
          <motion.div 
            className="w-full bg-secondary rounded-full h-3 overflow-hidden"
            animate={animateError ? {
              x: [0, -5, 5, -5, 5, 0],
            } : {}}
            transition={{ duration: 0.4 }}
          >
            <motion.div 
              className={`h-full rounded-full transition-all duration-300 ${totalWeight > 100 ? 'bg-red-500' : 'bg-primary'}`}
              style={{ 
                width: `${Math.min(totalWeight, 100)}%`
              }}
              animate={animateError ? {
                x: [0, -5, 5, -5, 5, 0]
              } : {}}
              transition={{ duration: 0.4 }}
            />
          </motion.div>
          
          {showErrorMessage && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-500 text-sm mt-2"
            >
              Максимальный лимит весов достигнут (100/100).
            </motion.p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterSliders; 